<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button @click.prevent="home" class="sci-btn-back" title="Torna al Gestionale">
          <i class="fas fa-arrow-left"></i>
          <span>Gestionale</span>
        </button>
        <div class="sci-title-group">
          <div class="sci-badge-tag">
            <i class="fas fa-clock"></i> Sala & Comande
          </div>
          <h1 class="sci-view-title">Tempi di Attesa & Capacità Cucina</h1>
          <p class="sci-view-subtitle">Configurazione dei tempi target di uscita per partita e gestione della capacità produttiva</p>
        </div>
      </div>
      <div class="sci-header-actions">
        <button class="sci-btn sci-btn-primary" @click="saveAll" :disabled="savingAll">
          <i class="fas fa-save" :class="{ 'fa-spin': savingAll }"></i>
          <span>{{ savingAll ? 'Salvataggio...' : 'Salva Tutte le Postazioni' }}</span>
        </button>
      </div>
    </header>

    <!-- Bento KPI Grid -->
    <section class="sci-kpi-grid">
      <div class="sci-kpi-card primary">
        <div class="kpi-icon-wrap blue">
          <i class="fas fa-utensils"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Partite Cucina</span>
          <span class="kpi-value">{{ categorie.length }}</span>
          <span class="kpi-sub">Linee di preparazione attive</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap green">
          <i class="fas fa-stopwatch"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Tempo Medio Ponderato</span>
          <span class="kpi-value">{{ averageTimeMinutes }} min</span>
          <span class="kpi-sub">Media calcolata su tutte le categorie</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap purple">
          <i class="fas fa-users-cog"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Addetti Totali Assegnati</span>
          <span class="kpi-value">{{ totalStaffCount }}</span>
          <span class="kpi-sub">Personale distribuito sulle postazioni</span>
        </div>
      </div>
    </section>

    <!-- Bento Stations Grid -->
    <section class="stations-grid">
      <div
        v-for="categoria in categorie"
        :key="categoria"
        class="sci-card station-card"
      >
        <!-- Station Header -->
        <div class="station-header">
          <div class="station-icon-wrap" :class="'icon-' + categoria">
            <i :class="getCategoryIcon(categoria)"></i>
          </div>
          <div class="station-title-box">
            <h3 class="station-name">{{ formatCategoryName(categoria) }}</h3>
            <span class="station-badge">Partita di Cucina</span>
          </div>
          <button
            class="sci-btn-action success"
            @click="save(categoria)"
            :disabled="savingCategory === categoria"
            title="Salva modifiche per questa postazione"
          >
            <i class="fas fa-check" v-if="savingCategory !== categoria"></i>
            <i class="fas fa-spinner fa-spin" v-else></i>
            <span>Salva</span>
          </button>
        </div>

        <!-- Station Inputs -->
        <div class="station-body">
          <!-- Timing Inputs -->
          <div class="timing-inputs-row">
            <div class="sci-field-group">
              <label><i class="far fa-clock"></i> Minuti Target:</label>
              <div class="sci-stepper">
                <button type="button" class="stepper-btn" @click="adjustValue(categoria, 'min', -1)">-</button>
                <input
                  type="number"
                  min="0"
                  max="120"
                  v-model.number="form[categoria].min"
                  class="stepper-input"
                />
                <button type="button" class="stepper-btn" @click="adjustValue(categoria, 'min', 1)">+</button>
              </div>
            </div>

            <div class="sci-field-group">
              <label><i class="fas fa-hourglass-start"></i> Secondi:</label>
              <div class="sci-stepper">
                <button type="button" class="stepper-btn" @click="adjustValue(categoria, 'secondi', -5)">-</button>
                <input
                  type="number"
                  min="0"
                  max="59"
                  v-model.number="form[categoria].secondi"
                  class="stepper-input"
                />
                <button type="button" class="stepper-btn" @click="adjustValue(categoria, 'secondi', 5)">+</button>
              </div>
            </div>
          </div>

          <!-- Capacity & Staff Inputs -->
          <div class="capacity-inputs-row">
            <div class="sci-field-group">
              <label><i class="fas fa-user-friends"></i> Addetti Postazione:</label>
              <div class="sci-stepper">
                <button type="button" class="stepper-btn" @click="adjustValue(categoria, 'numero_addetti_postazione', -1)">-</button>
                <input
                  type="number"
                  min="0"
                  max="20"
                  v-model.number="form[categoria].numero_addetti_postazione"
                  class="stepper-input"
                />
                <button type="button" class="stepper-btn" @click="adjustValue(categoria, 'numero_addetti_postazione', 1)">+</button>
              </div>
            </div>

            <div class="sci-field-group" v-if="['primi', 'secondi', 'pizza'].includes(categoria)">
              <label><i class="fas fa-layer-group"></i> Piatti in Parallelo:</label>
              <div class="sci-stepper">
                <button type="button" class="stepper-btn" @click="adjustValue(categoria, 'numero_in_parallelo', -1)">-</button>
                <input
                  type="number"
                  min="1"
                  max="50"
                  v-model.number="form[categoria].numero_in_parallelo"
                  class="stepper-input"
                />
                <button type="button" class="stepper-btn" @click="adjustValue(categoria, 'numero_in_parallelo', 1)">+</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Station Summary Footer -->
        <div class="station-footer">
          <small class="summary-text">
            Tempo totale impostato: <strong>{{ form[categoria].min }}m {{ form[categoria].secondi || 0 }}s</strong>
          </small>
        </div>
      </div>
    </section>

    <!-- Notification Toast -->
    <transition name="toast">
      <div v-if="notification" class="sci-toast" :class="'toast-' + notification.type">
        <i :class="notification.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle'"></i>
        <span>{{ notification.message }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
import { store } from '../store/store.js'

export default {
  name: 'TempiDiAttesa',
  data() {
    return {
      store,
      categorie: ['antipasti', 'primi', 'secondi', 'contorni', 'pizza', 'sushi', 'poke'],
      form: {
        antipasti: { min: 0, secondi: 0, numero_addetti_postazione: 0, numero_in_parallelo: 1 },
        primi: { min: 0, secondi: 0, numero_addetti_postazione: 0, numero_in_parallelo: 1 },
        secondi: { min: 0, secondi: 0, numero_addetti_postazione: 0, numero_in_parallelo: 1 },
        contorni: { min: 0, secondi: 0, numero_addetti_postazione: 0, numero_in_parallelo: 1 },
        pizza: { min: 0, secondi: 0, numero_addetti_postazione: 0, numero_in_parallelo: 1 },
        sushi: { min: 0, secondi: 0, numero_addetti_postazione: 0, numero_in_parallelo: 1 },
        poke: { min: 0, secondi: 0, numero_addetti_postazione: 0, numero_in_parallelo: 1 }
      },
      notification: null,
      savingCategory: null,
      savingAll: false
    }
  },
  computed: {
    averageTimeMinutes() {
      let totalSeconds = 0;
      this.categorie.forEach(cat => {
        const item = this.form[cat] || {};
        totalSeconds += ((Number(item.min) || 0) * 60) + (Number(item.secondi) || 0);
      });
      const avg = Math.round(totalSeconds / (this.categorie.length || 1) / 60);
      return avg || 0;
    },
    totalStaffCount() {
      return this.categorie.reduce((sum, cat) => {
        return sum + (Number(this.form[cat]?.numero_addetti_postazione) || 0);
      }, 0);
    }
  },
  methods: {
    home() {
      this.$router.push({ name: 'Gestionale' });
    },
    getToken() {
      try {
        if (!localStorage.token) return '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (parsed.token || localStorage.token);
      } catch (e) {
        return localStorage.token || '';
      }
    },
    formatCategoryName(cat) {
      if (!cat) return '';
      return cat.charAt(0).toUpperCase() + cat.slice(1);
    },
    getCategoryIcon(cat) {
      const map = {
        antipasti: 'fas fa-seedling',
        primi: 'fas fa-utensils',
        secondi: 'fas fa-drumstick-bite',
        contorni: 'fas fa-carrot',
        pizza: 'fas fa-pizza-slice',
        sushi: 'fas fa-fish',
        poke: 'fas fa-bowl-rice'
      };
      return map[cat] || 'fas fa-concierge-bell';
    },
    adjustValue(categoria, field, delta) {
      const cur = Number(this.form[categoria][field]) || 0;
      let next = cur + delta;
      if (field === 'secondi') {
        if (next < 0) next = 0;
        if (next > 59) next = 59;
      } else if (field === 'numero_in_parallelo') {
        if (next < 1) next = 1;
      } else {
        if (next < 0) next = 0;
      }
      this.form[categoria][field] = next;
    },
    async fetchTempi() {
      const token = this.getToken();
      try {
        const response = await this.axios.get(this.store.path + 'tempi-di-attesa', {
          headers: { authorization: `Bearer ${token}` }
        });
        if (response.data && response.data.length > 0) {
          response.data.forEach(item => {
            if (this.form[item.categorie_tempi_cucina]) {
              this.form[item.categorie_tempi_cucina] = {
                min: Number(item.min) || 0,
                secondi: Number(item.secondi) || 0,
                numero_addetti_postazione: Number(item.numero_addetti_postazione) || 0,
                numero_in_parallelo: Number(item.numero_in_parallelo) || 1
              };
            }
          });
        }
      } catch (error) {
        console.error('Errore fetch tempi:', error);
      }
    },
    async save(categoria) {
      this.savingCategory = categoria;
      const token = this.getToken();
      try {
        const payload = {
          categorie_tempi_cucina: categoria,
          ...this.form[categoria]
        };
        await this.axios.post(this.store.path + 'tempi-di-attesa', payload, {
          headers: { authorization: `Bearer ${token}` }
        });
        this.notify('Salvataggio completato per ' + this.formatCategoryName(categoria), 'success');
      } catch (error) {
        console.error('Errore salvataggio:', error);
        this.notify('Errore durante il salvataggio per ' + categoria, 'error');
      } finally {
        this.savingCategory = null;
      }
    },
    async saveAll() {
      this.savingAll = true;
      try {
        for (const cat of this.categorie) {
          await this.save(cat);
        }
        this.notify('Tutte le postazioni cucina sono state aggiornate con successo!', 'success');
      } catch (error) {
        this.notify('Errore durante il salvataggio multiplo', 'error');
      } finally {
        this.savingAll = false;
      }
    },
    notify(message, type) {
      this.notification = { message, type };
      setTimeout(() => {
        this.notification = null;
      }, 3500);
    }
  },
  created() {
    this.fetchTempi();
  }
}
</script>

<style scoped>
.sci-view-container {
  min-height: 100vh;
  background-color: #f1f5f9;
  padding: 24px 32px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #0f172a;
  box-sizing: border-box;
}

/* Header */
.sci-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  color: #334155;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.sci-btn-back:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-badge-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #0284c7;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.sci-view-title {
  font-size: 1.65rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
}

.sci-view-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 4px 0 0 0;
}

/* Buttons */
.sci-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.sci-btn-primary {
  background: #0284c7;
  color: #ffffff;
}

.sci-btn-primary:hover:not(:disabled) {
  background: #0369a1;
}

.sci-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Bento KPI Grid */
.sci-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.sci-kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.sci-kpi-card.primary {
  border-left: 4px solid #0284c7;
}

.kpi-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  flex-shrink: 0;
}

.kpi-icon-wrap.blue { background: #eff6ff; color: #2563eb; }
.kpi-icon-wrap.green { background: #f0fdf4; color: #16a34a; }
.kpi-icon-wrap.purple { background: #faf5ff; color: #7c3aed; }

.kpi-meta {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
}

.kpi-value {
  font-size: 1.6rem;
  font-weight: 800;
  color: #0f172a;
  margin: 2px 0;
}

.kpi-sub {
  font-size: 0.75rem;
  color: #94a3b8;
}

/* Stations Grid */
.stations-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
}

.station-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s;
}

.station-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.06);
}

.station-header {
  padding: 18px 20px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  gap: 14px;
  background: #f8fafc;
}

.station-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: #f1f5f9;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.icon-pizza { background: #fff7ed; color: #ea580c; }
.icon-primi { background: #fef2f2; color: #dc2626; }
.icon-secondi { background: #fff1f2; color: #e11d48; }
.icon-sushi { background: #ecfeff; color: #0891b2; }
.icon-poke { background: #f0fdf4; color: #16a34a; }
.icon-antipasti { background: #f0fdfa; color: #0d9488; }
.icon-contorni { background: #fefce8; color: #ca8a04; }

.station-title-box {
  flex: 1;
}

.station-name {
  font-size: 1.1rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
}

.station-badge {
  font-size: 0.75rem;
  color: #64748b;
}

.sci-btn-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  border: none;
  transition: all 0.2s;
}

.sci-btn-action.success {
  background: #dcfce7;
  color: #15803d;
}

.sci-btn-action.success:hover:not(:disabled) {
  background: #16a34a;
  color: #ffffff;
}

.station-body {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.timing-inputs-row,
.capacity-inputs-row {
  display: flex;
  gap: 14px;
}

.sci-field-group {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sci-field-group label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sci-stepper {
  display: flex;
  align-items: center;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  overflow: hidden;
}

.stepper-btn {
  width: 36px;
  height: 38px;
  background: transparent;
  border: none;
  font-size: 1.1rem;
  font-weight: 700;
  color: #475569;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}

.stepper-btn:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.stepper-input {
  flex: 1;
  width: 100%;
  border: none;
  background: transparent;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
  outline: none;
  padding: 0 4px;
}

.station-footer {
  padding: 12px 20px;
  border-top: 1px solid #f1f5f9;
  background: #fcfcfc;
  font-size: 0.8rem;
  color: #64748b;
}

/* Toast */
.sci-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 14px 20px;
  border-radius: 12px;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  z-index: 1100;
}

.toast-success {
  background: #0f172a;
  color: #4ade80;
  border: 1px solid #1e293b;
}

.toast-error {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.toast-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
